import { useRef, useState } from 'react'
import { Button, Callout, FieldRow, FormSection, Icon, Paragraph, StackList, StackListItem, StatusLine } from 'admination-react'
import { DateField } from '@/core/components/form/DateField'
import { lebenslaufZeilenOffen, type VerbisLebenslaufHinweis } from '../../api/verbisLebenslauf'
import type { LebenslaufZeitraumFailure } from './errors'
import type { LebenslaufOhneDatum } from './useVerbisLebenslaufStatus'

export const HINWEISE_TITEL = "Lebenslauf prüfen"

export const HINWEISE_TEXT = "Diese Angaben waren beim Auslesen nicht lesbar. Die Zeilen sind gespeichert, die Angabe fehlt."

export const OHNE_DATUM_TEXT =
  "Diese Zeilen haben noch kein Datum. Die Person bleibt in der ersten Spalte, bis Sie für jede Zeile ein Datum gespeichert haben."

export const VON_FEHLT_TEXT = "Bitte geben Sie an, seit wann."

const LEBENSLAUF_JAHRE = 70

export function hinweisText(hinweis: VerbisLebenslaufHinweis): string {
  return hinweis.datei != null ? `${hinweis.datei} · ${hinweis.grund}` : hinweis.grund
}

export function hinweiseToastText(hinweise: readonly VerbisLebenslaufHinweis[]): string {
  const zeilen = hinweise.length === 1 ? "1 Zeile" : `${hinweise.length} Zeilen`
  return `Lebenslauf: ${zeilen} ohne lesbare Angabe gespeichert. ${hinweise.map(hinweisText).join(' ')}`
}

function zeileTitel({ position, eintrag }: LebenslaufOhneDatum): string {
  return eintrag.taetigkeit ?? eintrag.eintragsart ?? `Eintrag ${position + 1}`
}

function zeileDetail({ eintrag }: LebenslaufOhneDatum): string {
  const teile = [eintrag.taetigkeit != null ? eintrag.eintragsart : null, eintrag.einrichtung]
  return teile.filter((teil): teil is string => teil != null).join(' · ')
}

export type ZeitraumSpeichern = (
  position: number,
  zeitraum: { von: string; bis: string | null },
) => Promise<LebenslaufZeitraumFailure | null>

export function LebenslaufHinweise({
  hinweise,
  ohneDatum,
  onSave,
}: {
  hinweise: readonly VerbisLebenslaufHinweis[]
  ohneDatum: readonly LebenslaufOhneDatum[]
  onSave: ZeitraumSpeichern
}) {
  return (
    <>
      <FormSection label={HINWEISE_TITEL}>
        {lebenslaufZeilenOffen(ohneDatum) && (
          <Callout tone="warning">{OHNE_DATUM_TEXT}</Callout>
        )}
        {hinweise.length > 0 && (
          <>
            <Paragraph variant="muted">{HINWEISE_TEXT}</Paragraph>
            <StackList aria-label="Beim Auslesen nicht lesbar">
              {hinweise.map((hinweis, i) => (
                <StackListItem
                  key={i}
                  end={hinweis.datei != null ? <StatusLine as="span">{hinweis.datei}</StatusLine> : undefined}
                >
                  {hinweis.grund}
                </StackListItem>
              ))}
            </StackList>
          </>
        )}
      </FormSection>
      {ohneDatum.map((zeile) => (
        <OhneDatumZeile key={zeile.position} zeile={zeile} onSave={onSave} />
      ))}
    </>
  )
}

function OhneDatumZeile({ zeile, onSave }: { zeile: LebenslaufOhneDatum; onSave: ZeitraumSpeichern }) {
  const [von, setVon] = useState(zeile.eintrag.von ?? '')
  const [bis, setBis] = useState(zeile.eintrag.bis ?? '')
  const [fehler, setFehler] = useState<LebenslaufZeitraumFailure['felder']>({})
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const detail = zeileDetail(zeile)

  async function speichern() {
    if (savingRef.current) return
    if (!von) {
      setFehler({ von: VON_FEHLT_TEXT })
      return
    }
    savingRef.current = true
    setSaving(true)
    setFehler({})
    try {
      const failure = await onSave(zeile.position, { von, bis: bis || null })
      if (failure) setFehler(failure.felder)
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  return (
    <FormSection label={zeileTitel(zeile)} headingLevel="h3">
      {detail && <Paragraph variant="muted">{detail}</Paragraph>}
      <DateField
        label="Von"
        required
        isoValue={von}
        onChange={setVon}
        pastYears={LEBENSLAUF_JAHRE}
        invalid={!!fehler.von}
        errorMessage={fehler.von}
      />
      <DateField
        label="Bis"
        isoValue={bis}
        onChange={setBis}
        pastYears={LEBENSLAUF_JAHRE}
        invalid={!!fehler.bis}
        errorMessage={fehler.bis}
        hint={{ tone: 'info', text: "Leer lassen, wenn es noch läuft." }}
      />
      <FieldRow>
        <Button variant="primary" loading={saving} onClick={() => void speichern()}>
          <Icon name="i-check" />
          Datum speichern</Button>
      </FieldRow>
    </FormSection>
  )
}
